// Search utility functions for text processing and matching

// Function to normalize text for searching
export const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().trim().replace(/\s+/g, ' ');
};

// Function to calculate Levenshtein distance for fuzzy matching
export const levenshteinDistance = (str1, str2) => {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
};

// Function to check if query matches text with fuzzy matching
export const fuzzyMatch = (query, text, threshold = 0.7) => {
  const normalizedQuery = normalizeText(query);
  const normalizedText = normalizeText(text);
  
  if (!normalizedQuery || !normalizedText) return false;
  
  // Direct substring match (highest priority)
  if (normalizedText.includes(normalizedQuery)) {
    return true;
  }
  
  // Word boundary matching
  const words = normalizedText.split(' ');
  const queryWords = normalizedQuery.split(' ');
  
  for (const queryWord of queryWords) {
    for (const word of words) {
      if (word.includes(queryWord) || queryWord.includes(word)) {
        return true;
      }
      
      // Fuzzy match for typos
      if (queryWord.length > 2 && word.length > 2) {
        const distance = levenshteinDistance(queryWord, word);
        const maxLength = Math.max(queryWord.length, word.length);
        const similarity = (maxLength - distance) / maxLength;
        
        if (similarity >= threshold) {
          return true;
        }
      }
    }
  }
  
  return false;
};

// Function to calculate match score for ranking
export const calculateMatchScore = (query, item) => {
  const normalizedQuery = normalizeText(query);
  let score = 0;
  
  // Title matches (highest weight)
  if (fuzzyMatch(normalizedQuery, item.title)) {
    score += 100;
    // Exact title match bonus
    if (normalizeText(item.title).includes(normalizedQuery)) {
      score += 50;
    }
  }
  
  // Description matches (medium weight)
  if (fuzzyMatch(normalizedQuery, item.description)) {
    score += 50;
    // Exact description match bonus
    if (normalizeText(item.description).includes(normalizedQuery)) {
      score += 25;
    }
  }
  
  // Keywords matches (medium weight)
  if (item.keywords && item.keywords.some(keyword => fuzzyMatch(normalizedQuery, keyword))) {
    score += 40;
    // Exact keyword match bonus
    if (item.keywords.some(keyword => normalizeText(keyword).includes(normalizedQuery))) {
      score += 20;
    }
  }
  
  // Content matches (lower weight)
  if (fuzzyMatch(normalizedQuery, item.content)) {
    score += 20;
    // Exact content match bonus
    if (normalizeText(item.content).includes(normalizedQuery)) {
      score += 10;
    }
  }
  
  // Category priority bonus
  if (item.category === 'Portfolio') score += 5;
  if (item.category === 'Main') score += 3;
  
  return score;
};

// Function to highlight matching text
export const highlightMatch = (text, query) => {
  if (!text || !query) return text;
  
  const normalizedQuery = normalizeText(query);
  const words = normalizedQuery.split(' ').filter(word => word.length > 0);
  
  let highlightedText = text;
  
  words.forEach(word => {
    const regex = new RegExp(`(${escapeRegExp(word)})`, 'gi');
    highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
  });
  
  return highlightedText;
};

// Function to escape special regex characters
export const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Function to truncate text with ellipsis
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
};

// Function to get search suggestions based on partial query
export const getSearchSuggestions = (query, searchData, limit = 5) => {
  if (!query || query.length < 2) return [];
  
  const normalizedQuery = normalizeText(query);
  const suggestions = new Set();
  
  searchData.forEach(item => {
    // Add title if it starts with query
    if (normalizeText(item.title).startsWith(normalizedQuery)) {
      suggestions.add(item.title);
    }
    
    // Add keywords that start with query
    if (item.keywords) {
      item.keywords.forEach(keyword => {
        if (normalizeText(keyword).startsWith(normalizedQuery)) {
          suggestions.add(keyword);
        }
      });
    }
  });
  
  return Array.from(suggestions).slice(0, limit);
};

// Function to check if search query is empty or whitespace
export const isValidQuery = (query) => {
  return query && typeof query === 'string' && query.trim().length > 0;
};